// Pure helpers for social-sync (#975): Graph → row mapping, scopes, error
// classes, insight-candidate selection. No imports, so vitest
// (src/test/unit/social-sync-mapping.test.ts) and tsc load the real module —
// same pattern as google-ads/report.ts.

export const SOCIAL_PLATFORMS = ["facebook", "instagram"] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];
export const PLATFORM_LABELS: Record<SocialPlatform, string> = { facebook: "Facebook", instagram: "Instagram" };
export const SOCIAL_MEDIA_TYPES = ["image", "video", "carousel", "text", "other"] as const;
export type SocialMediaType = (typeof SOCIAL_MEDIA_TYPES)[number];

export const SYNC_STATE_ID = "meta";
export const BACKFILL_MONTHS = 12;
export const RECENT_DAYS = 30; // insights refreshed every run
export const STALE_AFTER_MS = 20 * 3_600_000; // older posts: ~once a day
export const FAILED_RETRY_AFTER_MS = 24 * 3_600_000; // a failed post waits this long before it ranks with stale posts
export const MAX_INSIGHT_CALLS_PER_PLATFORM = 120; // per run
export const LOCK_STALE_MS = 10 * 60_000;

export const REQUIRED_SCOPES: Record<SocialPlatform, readonly string[]> = {
  facebook: ["pages_show_list", "pages_read_engagement"],
  instagram: ["pages_show_list", "instagram_basic", "instagram_manage_insights"],
};
// Scopes needed only for the insight phase (#975 audit I3). Missing one does NOT
// skip the listing: posts still upsert, only that platform's insight phase is
// skipped and scopeErrorMessage(platform, missing) is reported.
export const REQUIRED_INSIGHT_SCOPES: Record<SocialPlatform, readonly string[]> = {
  facebook: ["read_insights"],
  instagram: [],
};

// Alternatives in preference order; graph.ts falls back once per run when one
// is invalid. FB has no fallback: Meta retired the post impressions metrics
// for every API version on 2026-06-15, and asking for them now only yields 0s
// or errors (#1149).
export const FB_INSIGHT_GROUPS = {
  views: ["post_media_view"],
  reach: ["post_total_media_view_unique"],
} as const;
export const IG_INSIGHT_GROUPS = {
  views: ["views"],
  reach: ["reach"],
  saves: ["saved"],
  shares: ["shares"],
} as const;

export interface FbPost {
  id?: string;
  message?: string;
  created_time?: string;
  permalink_url?: string;
  full_picture?: string;
  status_type?: string;
  attachments?: { data?: Array<{ media_type?: string; type?: string }> };
  shares?: { count?: unknown };
  reactions?: { summary?: { total_count?: unknown } };
  comments?: { summary?: { total_count?: unknown } };
}

export interface IgMedia {
  id?: string;
  caption?: string;
  media_type?: string;
  media_product_type?: string;
  media_url?: string;
  thumbnail_url?: string;
  permalink?: string;
  timestamp?: string;
  like_count?: unknown;
  comments_count?: unknown;
}

export interface InsightEntry {
  name?: string;
  period?: string;
  values?: Array<{ value?: unknown }>;
  total_value?: { value?: unknown };
}

/** Listing fields, refreshed every run. FB carries shares here; IG gets shares from insights. */
export interface SocialPostListing {
  platform: SocialPlatform;
  platform_post_id: string;
  media_type: SocialMediaType;
  caption: string | null;
  permalink: string | null;
  thumbnail_url: string | null;
  posted_at: string;
  likes: number | null;
  comments: number | null;
  shares?: number | null;
  synced_at: string;
}

export interface InsightValues {
  views: number | null;
  reach: number | null;
  saves: number | null;
  shares?: number | null;
}

/** A finite, non-negative number (or numeric string) as a rounded integer; anything else is null. */
export function toCount(v: unknown): number | null {
  let n: number;
  if (typeof v === "number") n = v;
  else if (typeof v === "string" && v.trim() !== "") n = Number(v);
  else return null;
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n);
}

export function httpsOrNull(url: unknown): string | null {
  return typeof url === "string" && url.startsWith("https://") ? url : null;
}

function isoOrNull(ts: unknown): string | null {
  if (typeof ts !== "string" || ts === "") return null;
  const ms = Date.parse(ts);
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}

export function fbMediaType(p: FbPost): SocialMediaType {
  const first = p.attachments?.data?.[0];
  if (!first) return p.status_type === "added_video" ? "video" : "text";
  const kind = `${first.media_type ?? ""} ${first.type ?? ""}`.toLowerCase();
  if (kind.includes("album")) return "carousel";
  if (kind.includes("video") || kind.includes("reel")) return "video";
  if (kind.includes("photo") || kind.includes("image")) return "image";
  return "other";
}

export function igMediaType(m: IgMedia): SocialMediaType {
  if (m.media_type === "CAROUSEL_ALBUM") return "carousel";
  if (m.media_type === "VIDEO" || m.media_product_type === "REELS") return "video";
  if (m.media_type === "IMAGE") return "image";
  return "other";
}

function captionOrNull(text: unknown): string | null {
  return typeof text === "string" && text.trim() !== "" ? text.trim() : null;
}

export function mapFacebookPost(p: FbPost, syncedAt: string): SocialPostListing | null {
  const postedAt = isoOrNull(p.created_time);
  if (!p.id || !postedAt) return null;
  return {
    platform: "facebook",
    platform_post_id: p.id,
    media_type: fbMediaType(p),
    caption: captionOrNull(p.message),
    permalink: httpsOrNull(p.permalink_url),
    thumbnail_url: httpsOrNull(p.full_picture),
    posted_at: postedAt,
    likes: toCount(p.reactions?.summary?.total_count),
    comments: toCount(p.comments?.summary?.total_count),
    // Meta OMITS `shares` when the count is 0, so an absent field is 0;
    // a present-but-unreadable count is unknown.
    shares: p.shares === undefined ? 0 : toCount(p.shares.count),
    synced_at: syncedAt,
  };
}

export function mapInstagramMedia(m: IgMedia, syncedAt: string): SocialPostListing | null {
  const postedAt = isoOrNull(m.timestamp);
  if (!m.id || !postedAt) return null;
  const thumb = m.media_type === "VIDEO" ? m.thumbnail_url : (m.media_url ?? m.thumbnail_url);
  // No `shares` key at all: IG shares come from insights, and a listing-only
  // upsert must never write NULL over a stored count (#975 audit N4).
  return {
    platform: "instagram",
    platform_post_id: m.id,
    media_type: igMediaType(m),
    caption: captionOrNull(m.caption),
    permalink: httpsOrNull(m.permalink),
    thumbnail_url: httpsOrNull(thumb),
    posted_at: postedAt,
    likes: toCount(m.like_count), // null when the owner hid likes
    comments: toCount(m.comments_count),
    synced_at: syncedAt,
  };
}

export function igInsightCacheKey(m: IgMedia): string {
  return `instagram:${m.media_product_type ?? "?"}:${m.media_type ?? "?"}`;
}

export function readInsightValues(
  body: { data?: InsightEntry[] } | null | undefined,
): Record<string, number | null> {
  const out: Record<string, number | null> = {};
  const lifetime = new Set<string>();
  for (const entry of body?.data ?? []) {
    if (!entry.name) continue;
    // FB answers some metrics for several periods, e.g. reach as lifetime 225
    // then day [0, 0]. Keep the lifetime value (#1154).
    const isLifetime = entry.period === "lifetime";
    if (lifetime.has(entry.name) && !isLifetime) continue;
    if (isLifetime) lifetime.add(entry.name);
    out[entry.name] = toCount(entry.total_value?.value ?? entry.values?.[0]?.value);
  }
  return out;
}

export function pickGroupValues<G extends Record<string, readonly string[]>>(
  values: Record<string, number | null>,
  groups: G,
): Record<keyof G, number | null> {
  const out = {} as Record<keyof G, number | null>;
  for (const key of Object.keys(groups) as Array<keyof G>) {
    const hit = groups[key].find((name) => name in values);
    out[key] = hit === undefined ? null : values[hit];
  }
  return out;
}

export function fbInsightValues(values: Record<string, number | null>): InsightValues {
  const g = pickGroupValues(values, FB_INSIGHT_GROUPS);
  // Unique reach can't be 0 when the post has views, so that 0 is a placeholder (#1149).
  const reach = g.reach === 0 && (g.views ?? 0) > 0 ? null : g.reach;
  return { views: g.views, reach, saves: null }; // FB has no saves; shares come from the listing
}

/** Meta sent a literal 0 for FB reach; index.ts logs these (#1149). */
export function fbReachZeroFromMeta(values: Record<string, number | null>): boolean {
  return pickGroupValues(values, FB_INSIGHT_GROUPS).reach === 0;
}

export function igInsightValues(values: Record<string, number | null>): InsightValues {
  const g = pickGroupValues(values, IG_INSIGHT_GROUPS);
  return { views: g.views, reach: g.reach, saves: g.saves, shares: g.shares };
}

/**
 * Which posts get an insight call this run, highest priority first:
 * tier 0 never fetched, tier 1 posted in the last RECENT_DAYS, tier 2 last
 * fetched at least STALE_AFTER_MS ago. Anything else waits for a later run.
 * A post whose last insight call failed (`lastFailed`, #1145) is tier 3 until
 * the failure is FAILED_RETRY_AFTER_MS old, then tier 2, so posts that always
 * fail can't take the budget from the rest.
 */
export function pickInsightCandidates(
  rows: Array<Pick<SocialPostListing, "platform_post_id" | "posted_at">>,
  lastFetched: Map<string, string | null>,
  nowMs: number,
  cap: number,
  lastFailed: Map<string, string | null> = new Map(),
): string[] {
  const recentCutoff = nowMs - RECENT_DAYS * 86_400_000;
  const tiered: Array<{ id: string; tier: number; postedMs: number }> = [];
  for (const row of rows) {
    const postedMs = Date.parse(row.posted_at);
    const fetched = lastFetched.get(row.platform_post_id) ?? null;
    const fetchedMs = fetched ? Date.parse(fetched) : NaN;
    const failed = lastFailed.get(row.platform_post_id) ?? null;
    const failedMs = failed ? Date.parse(failed) : NaN;
    let tier: number | null = null;
    if (!Number.isNaN(failedMs)) tier = nowMs - failedMs >= FAILED_RETRY_AFTER_MS ? 2 : 3;
    else if (Number.isNaN(fetchedMs)) tier = 0;
    else if (postedMs >= recentCutoff) tier = 1;
    else if (nowMs - fetchedMs >= STALE_AFTER_MS) tier = 2;
    if (tier !== null) tiered.push({ id: row.platform_post_id, tier, postedMs });
  }
  tiered.sort((a, b) => a.tier - b.tier || b.postedMs - a.postedMs);
  return tiered.slice(0, cap).map((t) => t.id);
}

/** First occurrence wins: one upsert can't touch the same (platform, id) twice. */
export function dedupeListings(rows: SocialPostListing[]): SocialPostListing[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    if (seen.has(row.platform_post_id)) return false;
    seen.add(row.platform_post_id);
    return true;
  });
}

export type FullRow = SocialPostListing & InsightValues & { metrics_updated_at: string; insights_failed_at: null };
export type FailedRow = SocialPostListing & { insights_failed_at: string };

/**
 * Posts whose insights were fetched this run become full rows (clearing any
 * past failure); posts whose insight call failed (`failedIds`) record the
 * failure time; the rest stay listing-only. Upserts only set the columns
 * they're sent, so listing-only and failed rows never overwrite stored
 * insight values.
 */
export function splitRows(
  listings: SocialPostListing[],
  insights: Map<string, InsightValues>,
  syncedAt: string,
  failedIds: ReadonlySet<string> = new Set(),
): { fullRows: FullRow[]; failedRows: FailedRow[]; listingRows: SocialPostListing[] } {
  const fullRows: FullRow[] = [];
  const failedRows: FailedRow[] = [];
  const listingRows: SocialPostListing[] = [];
  for (const row of listings) {
    const values = insights.get(row.platform_post_id);
    if (values) fullRows.push({ ...row, ...values, metrics_updated_at: syncedAt, insights_failed_at: null });
    else if (failedIds.has(row.platform_post_id)) failedRows.push({ ...row, insights_failed_at: syncedAt });
    else listingRows.push(row);
  }
  return { fullRows, failedRows, listingRows };
}

export interface MetricSnapshot {
  social_post_id: string;
  captured_on: string;
  captured_at: string;
  views: number | null;
  reach: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  saves: number | null;
}

/** One snapshot per saved full row, keyed by the UTC date of `capturedAt`. */
export function buildSnapshots(
  saved: Array<{ id: string; platform_post_id: string }>,
  fullRows: FullRow[],
  capturedAt: string,
): MetricSnapshot[] {
  const byPostId = new Map(fullRows.map((r) => [r.platform_post_id, r]));
  const snapshots: MetricSnapshot[] = [];
  for (const s of saved) {
    const r = byPostId.get(s.platform_post_id);
    if (!r) continue;
    snapshots.push({
      social_post_id: s.id,
      captured_on: capturedAt.slice(0, 10),
      captured_at: capturedAt,
      views: r.views,
      reach: r.reach,
      likes: r.likes,
      comments: r.comments,
      shares: r.shares ?? null,
      saves: r.saves,
    });
  }
  return snapshots;
}

/** `required` minus the permissions the token has with status "granted". */
export function missingScopes(
  perms: Array<{ permission?: string; status?: string }>,
  required: readonly string[],
): string[] {
  const granted = new Set(perms.filter((p) => p.status === "granted").map((p) => p.permission));
  return required.filter((scope) => !granted.has(scope));
}

export function scopeErrorMessage(platform: SocialPlatform, missing: string[]): string {
  return `${PLATFORM_LABELS[platform]} sync stopped: the Meta token is missing permission ${missing.join(", ")}. Grant it to the system user in Meta Business Settings and regenerate META_SYSTEM_USER_TOKEN.`;
}

export interface PageAccount {
  id?: string;
  name?: string;
  access_token?: string;
  instagram_business_account?: { id?: string };
}

export type PickPageResult =
  | { ok: true; pageId: string; pageToken: string; igUserId: string | null }
  | { ok: false; error: string };

function pageResult(page: PageAccount): PickPageResult {
  if (!page.id || !page.access_token) {
    return { ok: false, error: `The Meta token can't get a Page access token for Page ${page.id ?? "?"}` };
  }
  return {
    ok: true,
    pageId: page.id,
    pageToken: page.access_token,
    igUserId: page.instagram_business_account?.id ?? null,
  };
}

/**
 * Chooses the Facebook Page to sync. META_PAGE_ID pins it; `fallback` is the
 * GET /{META_PAGE_ID} account index.ts fetches when the pinned Page isn't in
 * /me/accounts. With no pin, the token must see exactly one Page.
 */
export function pickPage(
  accounts: PageAccount[],
  envPageId: string | undefined,
  fallback?: PageAccount | null,
): PickPageResult {
  if (envPageId) {
    const match = accounts.find((a) => a.id === envPageId);
    if (match) return pageResult(match);
    if (fallback && fallback.id === envPageId && fallback.access_token) return pageResult(fallback);
    return { ok: false, error: `META_PAGE_ID ${envPageId} isn't among the Pages this token can see` };
  }
  if (accounts.length === 0) {
    return {
      ok: false,
      error:
        "The Meta token can't see any Facebook Page (pages_show_list, or the Page isn't assigned to the system user)",
    };
  }
  if (accounts.length > 1) {
    return { ok: false, error: `The Meta token can see ${accounts.length} Pages; set META_PAGE_ID to choose one` };
  }
  return pageResult(accounts[0]);
}

export type GraphErrorKind = "auth" | "permission" | "rate_limit" | "invalid_metric" | "unsupported_media" | "other";

export function classifyGraphError(e: { code?: number; subcode?: number; message?: string }): GraphErrorKind {
  const code = e.code;
  if (code === 190) return "auth";
  // IG media posted before the account became a business account has no insights.
  if (e.subcode === 2108006) return "unsupported_media";
  if (code === 10 || (code !== undefined && code >= 200 && code <= 299)) return "permission";
  if (code === 4 || code === 17 || code === 32 || code === 613 || (code !== undefined && code >= 80000 && code <= 80014)) {
    return "rate_limit";
  }
  if (code === 100 && /metric/i.test(e.message ?? "")) return "invalid_metric";
  return "other";
}

export type PermissionsResult =
  | { ok: true; granted: Array<{ permission?: string; status?: string }> }
  | { ok: false; kind: GraphErrorKind; message: string };

export interface PlatformPlan {
  run: boolean;
  skipInsights: boolean;
  error: string | null;
}

export const IG_NOT_LINKED_MESSAGE = "The Facebook Page isn't linked to an Instagram professional account";

/**
 * The per-platform decision before any listing call. Only an auth failure
 * (#190) of the permissions check stops both platforms; any other failure
 * skips the pre-check and leaves scope problems to the runtime Graph errors.
 */
export function planPlatforms(
  perms: PermissionsResult,
  igUserId: string | null,
): Record<SocialPlatform, PlatformPlan> {
  if (!perms.ok && perms.kind === "auth") {
    const stop: PlatformPlan = { run: false, skipInsights: true, error: perms.message };
    return { facebook: { ...stop }, instagram: { ...stop } };
  }
  const plan = {} as Record<SocialPlatform, PlatformPlan>;
  for (const platform of SOCIAL_PLATFORMS) {
    if (platform === "instagram" && igUserId === null) {
      plan[platform] = { run: false, skipInsights: true, error: IG_NOT_LINKED_MESSAGE };
      continue;
    }
    if (!perms.ok) {
      plan[platform] = { run: true, skipInsights: false, error: null };
      continue;
    }
    const missing = missingScopes(perms.granted, REQUIRED_SCOPES[platform]);
    if (missing.length > 0) {
      plan[platform] = { run: false, skipInsights: true, error: scopeErrorMessage(platform, missing) };
      continue;
    }
    const missingInsight = missingScopes(perms.granted, REQUIRED_INSIGHT_SCOPES[platform]);
    plan[platform] =
      missingInsight.length > 0
        ? { run: true, skipInsights: true, error: scopeErrorMessage(platform, missingInsight) }
        : { run: true, skipInsights: false, error: null };
  }
  return plan;
}

export function sanitizeGraphMessage(msg: string | undefined): string {
  return (msg ?? "unknown").replace(/access_token=[^&\s"']+/gi, "access_token=***").slice(0, 300);
}

/** `nowMs` minus `months` calendar months, in UTC. */
export function cutoffMs(nowMs: number, months = BACKFILL_MONTHS): number {
  const d = new Date(nowMs);
  d.setUTCMonth(d.getUTCMonth() - months);
  return d.getTime();
}
